import React, { useLayoutEffect, useRef, useState } from 'react';
import { SparkIcon, TooltipTrigger } from '@spark';
import OutputCell from './OutputCell';
import { estimateTokens, playgroundOutputKey, rowNeedsExpandButton } from './playgroundUtils';

/**
 * One examples grid row (Figma .Playground-Data-Row 1287:54103): editable
 * input cell with a `Tokens: N in` footer, one output cell per model slot,
 * and hover-only remove (top-right) / expand (bottom-right) controls.
 * Expand is shown only when the example or a response overflows the
 * collapsed 163px clamp (or the row is already expanded, so Collapse
 * remains available). Expanding grows the row in place so the full input
 * and outputs are visible, capped at a total row height of 650px (see
 * playground.css expand view).
 */
const DataRow = ({
    row,
    slots,
    outputs,
    taskType,
    allLabels,
    expanded,
    onInputChange,
    onRemoveRow,
    onExpandRow,
    onToggleOutputView,
    onInspectLogic,
    benchmarkMode,
    selectedOutputs,
    onToggleBenchmark,
}) => {
    const rowRef = useRef(null);
    const textareaRef = useRef(null);
    const [contentOverflows, setContentOverflows] = useState(false);

    // In the expanded state the textarea grows to fit its content (the CSS
    // min-height keeps it from collapsing below the default row height, and
    // the CSS max-height clamps it so the row stays within its 650px cap).
    // Measure overflow against the collapsed 163px clamp so Expand only
    // appears when the example or a response is actually clipped.
    useLayoutEffect(() => {
        const textarea = textareaRef.current;
        const rowEl = rowRef.current;
        if (textarea) {
            if (expanded) {
                textarea.style.height = 'auto';
                textarea.style.height = `${textarea.scrollHeight}px`;
            } else {
                textarea.style.height = '';
            }
        }

        const measure = () => {
            if (expanded) return;
            const outputEls = rowEl
                ? Array.from(rowEl.querySelectorAll('.pg-output-cell__text, .pg-ner'))
                : [];
            setContentOverflows(
                rowNeedsExpandButton({ expanded: false, textarea, outputEls })
            );
        };

        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;

        let rafId = 0;
        const scheduleMeasure = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(measure);
        };
        const observer = new ResizeObserver(scheduleMeasure);
        if (rowEl) observer.observe(rowEl);
        if (textarea) observer.observe(textarea);
        return () => {
            cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, [expanded, row.value, outputs, slots]);

    const showExpandControl = expanded || contentOverflows;

    return (
        <div
            ref={rowRef}
            className={`pg-row fs-unmask${expanded ? ' pg-row--expanded' : ''}`}
        >
            <div className="pg-row__body">
                <div className="pg-row__input-col">
                    <textarea
                        ref={textareaRef}
                        className="pg-row__textarea"
                        value={row.value}
                        placeholder="Type your input text to test"
                        aria-label="Example input"
                        onChange={(e) => onInputChange(row.id, e.target.value)}
                    />
                    <span className="pg-row__tokens">
                        {`Tokens: ${estimateTokens(row.value)} in`}
                    </span>
                </div>
                {slots.map((slot) => {
                    const key = playgroundOutputKey(row.id, slot.slotId);
                    return (
                        <OutputCell
                            key={slot.slotId}
                            output={outputs?.[slot.slotId]}
                            taskType={taskType}
                            inputText={row.value}
                            allLabels={allLabels}
                            isCommercial={slot.provider !== 'specificai'}
                            onToggleView={() => onToggleOutputView(row.id, slot.slotId)}
                            onInspectLogic={
                                onInspectLogic
                                    ? () => onInspectLogic(row.id, slot.slotId)
                                    : undefined
                            }
                            benchmarkMode={benchmarkMode}
                            benchmarkSelected={selectedOutputs?.has(key)}
                            onToggleBenchmark={() => onToggleBenchmark(row.id, slot.slotId)}
                        />
                    );
                })}
            </div>
            {/* The TooltipTrigger span is the positioned element (it has its
                own `position: relative`), so the corner anchoring lives on
                the wrapper classes, not on the buttons. */}
            <TooltipTrigger
                text="Remove example"
                placement="bottom"
                className="pg-row__close-anchor"
            >
                <button
                    type="button"
                    className="pg-row__close"
                    aria-label="Remove example"
                    onClick={() => onRemoveRow(row.id)}
                >
                    <SparkIcon slug="close" size="small" />
                </button>
            </TooltipTrigger>
            {showExpandControl && (
                <TooltipTrigger
                    text={expanded ? 'Collapse' : 'Expand'}
                    placement="top"
                    className="pg-row__expand-anchor"
                >
                    <button
                        type="button"
                        className="pg-row__expand"
                        aria-label={expanded ? 'Collapse example' : 'Expand example'}
                        onClick={() => onExpandRow(row.id)}
                    >
                        <SparkIcon
                            slug={expanded ? 'minimize-arrows' : 'maximize-arrow'}
                            size="medium"
                        />
                    </button>
                </TooltipTrigger>
            )}
        </div>
    );
};

export default DataRow;
